import { DataType } from '@square/ignition/dist/types/types/data-type';
import { useApiExplorerDropdownOptionsForField } from '@squareup/dex-ui-shared-api-explorer-providers';
import React, { FC, memo } from 'react';

import { ApiExplorerBooleanField } from './primitives/ApiExplorerBooleanField';
import { ApiExplorerDatetimeField } from './primitives/ApiExplorerDatetimeField';
import { ApiExplorerIdempotencyField } from './primitives/ApiExplorerIdempotencyKeyField';
import { ApiExplorerStringField } from './primitives/ApiExplorerStringField';
import { ApiExplorerPrimitiveFieldProps } from './props';
import { useApiExplorerFieldContext } from './providers/ApiExplorerFieldProvider';

interface Props extends Omit<ApiExplorerPrimitiveFieldProps, 'isNullable'> {
  isEmptyStringAllowed?: boolean | undefined;
}

const ApiExplorerPrimitiveField: FC<Props> = memo(
  ({
    name,
    type = 'string' as unknown as DataType,
    currentValue,
    onChange,
    path,
    actionButton,
    isEmptyStringAllowed = true,
    datetimeFormat,
  }) => {
    const { isNullable } = useApiExplorerFieldContext();
    const dropdownOptions = useApiExplorerDropdownOptionsForField(name);

    const isBoolean = type === 'boolean';

    if (isBoolean) {
      return (
        <ApiExplorerBooleanField
          currentValue={currentValue}
          onChange={onChange}
          path={path}
          name={name}
          type={type}
          isNullable={isNullable}
        />
      );
    } else if (name === 'idempotency_key') {
      return (
        <ApiExplorerIdempotencyField
          currentValue={currentValue}
          onChange={onChange}
          path={path}
          name={name}
          type={type}
          actionButton={actionButton}
          isNullable={isNullable}
        />
      );
    } else if (datetimeFormat) {
      return (
        <ApiExplorerDatetimeField
          currentValue={currentValue}
          onChange={onChange}
          path={path}
          name={name}
          type={type}
          datetimeFormat={datetimeFormat}
          actionButton={actionButton}
          isNullable={isNullable}
        />
      );
    } else {
      return (
        <ApiExplorerStringField
          currentValue={currentValue}
          onChange={onChange}
          path={path}
          name={name}
          type={type}
          actionButton={actionButton}
          dropdownValues={dropdownOptions ?? undefined}
          isNullable={isNullable}
          isEmptyStringAllowed={isEmptyStringAllowed}
        />
      );
    }
  }
);

ApiExplorerPrimitiveField.displayName = 'ApiExplorerPrimitiveField';

export { ApiExplorerPrimitiveField };
